import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import { ELIGIBLE_PERMISSIONS } from '../GscTools/query';
import { readParams, writeParams } from '../GscTools/url';
import Loading from '../../../components/Loading';

import EvidenceBlock from '../GscTools/components/EvidenceBlock';
import ExtraControls from './components/ExtraControls';
import { CANONICAL_COLUMNS, REQUEST_COLUMNS, RICH_COLUMNS, STATUS_COLUMNS } from './keys';
import { inspectUrl } from './query';
import {
    canonicalMatch,
    canonicalRows,
    formatTime,
    readState,
    requestRows,
    richResultRows,
    statusRows,
} from './rows';
import {
    URL_KEYS,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';

/**
 * URL Inspection — Google's indexed evidence for one exact URL of one property.
 *
 * Indexed evidence only. What comes back is what Google holds about the indexed version of the
 * URL: it is not a live test of the page as it stands, and inspecting never asks Google to index
 * anything. The page says so wherever a reader could take it for either.
 *
 * One URL, one request, and only when the user presses Inspect. Search Console meters URL
 * Inspection per property, so a link that names a URL fills the field without spending quota, a
 * request in flight cannot be sent again, and nothing is retried or batched on the user's behalf.
 *
 * Two times are kept apart throughout: when GDDash asked (the inspection time, block 01) and when
 * Google last crawled the URL (block 02). They are different moments, and the first says nothing
 * about how fresh the second is.
 *
 * A result belongs to the URL that produced it. Inspecting a different URL clears the previous
 * evidence at once, and so does editing the URL or the property in the bar — evidence for one URL
 * must never sit under another URL typed above it. Re-inspecting the same URL keeps the previous
 * result until the new one lands, and if the new one fails — a spent quota above all — the
 * previous result stays on screen labelled as not refreshed rather than being passed off as new.
 * Nothing is stored: the result lives in this page for as long as it is open.
 */
class UrlInspection extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();

        // Bumped by every inspection; a response carrying an older value is stale.
        this.generation = 0;
        this.isMounted_ = false;

        this.urlDraft = reportDraftFromParams(readParams());

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,

            draft: { ...initialDraft(), ...this.urlDraft },

            // The scope of the most recent request sent, settled or not.
            applied: null,
            loading: false,
            // Held apart from the result: a failed re-inspection reports itself without discarding
            // the last good result for the same URL. `quota` marks a spent inspection quota.
            error: null,
            // The most recent successful inspection, exactly as the endpoint returned it.
            result: null,
            controlsOpen: true,
            capturing: false,
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read falls back to the first one, and the
        // bar is open for the user to check it — nothing is inspected until they press Inspect.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];

        this.setState((state) => ({
            properties,
            propertiesError,
            loadingProperties: false,
            draft: property
                ? {
                      ...state.draft,
                      siteUrl: property.siteUrl,
                      permissionLevel: property.permissionLevel,
                  }
                : state.draft,
        }));
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    handleDraftChange(patch) {
        this.setState((state) => {
            const draft = { ...state.draft, ...patch };

            if (patch.siteUrl) {
                const property = state.properties.find((item) => item.siteUrl === patch.siteUrl);
                draft.permissionLevel = property?.permissionLevel ?? '';
            }

            return { draft };
        });
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    /** Inspect is held to the same checks as the button, so Enter in the URL field is too. */
    handleApply() {
        const scope = this.scope();
        if (!scope.ready || !this.credentialsDomainId) return;
        if (this.state.loading || this.state.loadingProperties) return;
        this.load(scope);
    }

    /** Re-inspects the URL on screen, under the scope it was inspected with. */
    handleRefresh() {
        if (this.state.applied && !this.state.loading) this.load(this.state.applied);
    }

    /** Whether a result is the answer for this scope's URL, under this scope's property. */
    resultFor(scope) {
        const result = this.state.result;
        return (
            !!result &&
            !!scope &&
            result.site_url === scope.siteUrl &&
            result.inspection_url === scope.inspectionUrl
        );
    }

    async load(scope) {
        this.generation += 1;
        const generation = this.generation;

        // Only a scope that was sent is written to the address, so a shared link always names a
        // URL someone actually inspected — and opening it fills the field, nothing more.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        // The bar stays open: inspecting is usually followed by the next URL.
        const keepResult = this.resultFor(scope);
        this.setState((state) => ({
            applied: scope,
            loading: true,
            error: null,
            result: keepResult ? state.result : null,
        }));

        try {
            const result = await inspectUrl({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl: scope.siteUrl,
                inspectionUrl: scope.inspectionUrl,
                languageCode: scope.languageCode,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            // Replaced in one step, so the blocks never show half of one result and half of another.
            this.setState({ loading: false, error: null, result });
        } catch (err) {
            console.log('url inspection', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                loading: false,
                error: {
                    message: err?.message ?? this.props.t('global.something_went_wrong'),
                    quota: !!err?.quota,
                },
            });
        }
    }

    exportName() {
        const result = this.state.result;
        return `url-inspection-${result?.inspection_url ?? 'url'}-${
            result?.inspected_at ? moment(result.inspected_at).format('YYYY-MM-DD-HHmm') : ''
        }`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the evidence on screen as an image — no further request and no upload. The controls
     * and export actions are left out of the frame; the inspected URL and the inspection time stay
     * in it, so the picture still says what was inspected and when.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName()}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    /** What the collapsed scope bar states, since this report has no window to put there. */
    summary() {
        const { t } = this.props;
        const result = this.state.result;

        return result ? (
            <>
                {result.inspection_url}
                <small>
                    {t('tools.url_inspection.inspected_at', {
                        time: formatTime(result.inspected_at),
                    })}
                </small>
            </>
        ) : (
            <>{t('tools.url_inspection.summary_idle')}</>
        );
    }

    renderState(icon, title, body, error) {
        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-body gsc-tool-load-state">
                    <span className="gsc-tool-load-icon" aria-hidden="true">
                        <i className={icon} />
                    </span>
                    <h4>{title}</h4>
                    {body}
                    {!!error && <div className="gsc-tool-banner error">{error}</div>}
                </div>
            </div>
        );
    }

    renderReport() {
        const { t } = this.props;
        const { result, error, loading } = this.state;
        const inspectedAt = formatTime(result.inspected_at);
        const fileName = (title) => `${title} — ${this.exportName()}`;

        const rich = result.inspection_result?.richResultsResult;
        const richVerdict = readState(t, 'verdict', rich?.verdict);
        const match = canonicalMatch(result);

        return (
            <>
                {loading && (
                    <div className="gsc-tool-banner">
                        {t('tools.url_inspection.reinspecting', { time: inspectedAt })}
                    </div>
                )}

                {/* A failed re-inspection is never presented as a new result: the evidence below is
                    the previous one, and the banner says when it is from. */}
                {!!error && (
                    <div className="gsc-tool-banner error">
                        {error.quota
                            ? t('tools.url_inspection.stale_quota', { time: inspectedAt })
                            : t('tools.url_inspection.stale_error', {
                                  time: inspectedAt,
                                  reason: error.message,
                              })}
                    </div>
                )}

                <EvidenceBlock
                    number="01"
                    title={t('tools.url_inspection.request.title')}
                    description={t('tools.url_inspection.request.description')}
                    columns={REQUEST_COLUMNS(t)}
                    rows={requestRows(t, result)}
                    fileName={fileName(t('tools.url_inspection.request.title'))}
                    notes={[
                        t('tools.url_inspection.request.not_live'),
                        t('tools.url_inspection.request.single_url'),
                    ]}
                />

                <EvidenceBlock
                    number="02"
                    title={t('tools.url_inspection.status.title')}
                    description={t('tools.url_inspection.status.description', {
                        time: inspectedAt,
                    })}
                    columns={STATUS_COLUMNS(t)}
                    rows={statusRows(t, result)}
                    fileName={fileName(t('tools.url_inspection.status.title'))}
                    notes={[
                        t('tools.url_inspection.status.raw_note'),
                        t('tools.url_inspection.status.unknown_note'),
                        t('tools.url_inspection.status.no_history'),
                    ]}
                />

                <EvidenceBlock
                    number="03"
                    title={t('tools.url_inspection.canonical.title')}
                    description={t('tools.url_inspection.canonical.description')}
                    columns={CANONICAL_COLUMNS(t)}
                    rows={canonicalRows(t, result)}
                    fileName={fileName(t('tools.url_inspection.canonical.title'))}
                    banner={
                        match === 'different' && (
                            <div className="gsc-tool-banner">
                                {t('tools.url_inspection.canonical.different_banner')}
                            </div>
                        )
                    }
                    notes={[
                        t('tools.url_inspection.canonical.string_note'),
                        t('tools.url_inspection.canonical.not_orphaned'),
                    ]}
                />

                <EvidenceBlock
                    number="04"
                    title={t('tools.url_inspection.rich.title')}
                    description={t('tools.url_inspection.rich.description')}
                    columns={RICH_COLUMNS(t)}
                    rows={richResultRows(t, result)}
                    fileName={fileName(t('tools.url_inspection.rich.title'))}
                    emptyMessage={
                        rich
                            ? t('tools.url_inspection.rich.none_detected')
                            : t('tools.url_inspection.rich.not_returned')
                    }
                    banner={
                        !!rich && (
                            <p className="gsc-tool-table-note">
                                {t('tools.url_inspection.rich.verdict', {
                                    verdict: richVerdict.reading,
                                })}
                            </p>
                        )
                    }
                    notes={[
                        t('tools.url_inspection.rich.coverage'),
                        t('tools.url_inspection.rich.history'),
                        t('tools.url_inspection.rich.no_audit'),
                    ]}
                />

                <p className="gsc-tool-table-note">{t('tools.url_inspection.footnote')}</p>
            </>
        );
    }

    renderBody(scope) {
        const { t } = this.props;
        const { applied, result, loading, error } = this.state;

        if (!applied) {
            return this.renderState(
                'icon-fi_search',
                t('tools.url_inspection.idle_title'),
                <p>{t('tools.url_inspection.idle')}</p>,
            );
        }

        // Evidence is only ever shown under the URL and property that produced it.
        if (result && !loading && !this.resultFor(scope)) {
            return this.renderState(
                'icon-fi_search',
                t('tools.url_inspection.changed_title'),
                <p>{t('tools.url_inspection.changed')}</p>,
            );
        }

        if (!result && loading) {
            return this.renderState(
                'icon-fi_search',
                t('tools.url_inspection.loading_title'),
                <Loading padding={10} />,
            );
        }

        if (!result) {
            const reason = error?.quota ? 'quota' : 'failed';
            return this.renderState(
                'icon-fi_search',
                t('tools.url_inspection.failed_title'),
                <p>{t(`tools.url_inspection.${reason}`)}</p>,
                error?.message,
            );
        }

        return this.renderReport();
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = !!this.state.result && this.resultFor(scope);

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.url_inspection.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.url_inspection.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    // No window, no surface, no comparison and no daily data state: the bar offers
                    // the property and this report's URL and language, and states the inspected
                    // URL where the date range would otherwise be.
                    fields={['property']}
                    hidePreliminary
                    summary={this.summary()}
                    applyLabel={t('tools.url_inspection.controls.inspect')}
                    applyDisabled={!scope.ready || !this.credentialsDomainId}
                    extraScopeLabels={applied ? [applied.inspectionUrl, applied.languageCode] : []}
                >
                    <ExtraControls
                        draft={this.state.draft}
                        onChange={this.handleDraftChange}
                        onSubmit={this.handleApply}
                    />
                </ScopeControls>

                {this.renderBody(scope)}
            </div>
        );
    }
}

export default withTranslation()(UrlInspection);
